/**
 * Effects Lab Page
 * Dedicated page for testing and previewing guitar effects with real audio
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { AudioTestPanel } from '../components/studio/effects/AudioTestPanel';
import { GuitarPresetSelector } from '../components/studio/effects/GuitarPresetSelector';
import { GuitarAmpSimulator } from '../components/studio/effects/GuitarAmpSimulator';
import { GuitarPedalBoard } from '../components/studio/effects/GuitarPedalBoard';
import { GuitarEffectsChain, FACTORY_PRESETS, applyPresetToTrack } from '../components/studio/effects';
import {
  ArrowLeft,
  Guitar,
  Zap,
  Music,
  Save,
  Share,
  Settings,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EffectsLabPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [guitarEffects, setGuitarEffects] = useState({
    enabled: true,
    ampEnabled: true,
    ampModel: 'crunch',
    cabinet: '4x12',
    ampControls: { gain: 50, bass: 50, mid: 50, treble: 50, presence: 50, master: 70 },
    pedals: [],
  });
  const [effectsStatus, setEffectsStatus] = useState(null);
  const [userPresets, setUserPresets] = useState([]);
  
  // Audio chain ref
  const effectsChainRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Initialize audio context and effects chain
  useEffect(() => {
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      if (!effectsChainRef.current && audioContextRef.current) {
        effectsChainRef.current = new GuitarEffectsChain(audioContextRef.current);
        effectsChainRef.current.enable();
        
        // Apply default settings
        if (guitarEffects.ampEnabled) {
          effectsChainRef.current.setAmpEnabled(true, {
            ampModel: guitarEffects.ampModel,
            cabinet: guitarEffects.cabinet,
            controls: guitarEffects.ampControls,
          });
        }
      }
    };
    
    // Initialize on user interaction
    const handleInteraction = () => {
      initAudio();
      document.removeEventListener('click', handleInteraction);
    };
    
    document.addEventListener('click', handleInteraction);
    
    return () => {
      document.removeEventListener('click', handleInteraction);
      if (effectsChainRef.current) {
        effectsChainRef.current.dispose();
      }
    };
  }, []);
  
  // Fetch user presets
  useEffect(() => {
    const fetchPresets = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(`${API_URL}/api/guitar-presets/?user_id=${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setUserPresets(data.presets || []);
        }
      } catch (err) {
        console.error('Failed to fetch presets:', err);
      }
    };
    fetchPresets();
  }, [user?.id]);
  
  // Handle preset selection
  const handlePresetSelect = useCallback((preset) => {
    setSelectedPreset(preset);
    const settings = applyPresetToTrack(preset);
    if (settings) {
      setGuitarEffects(settings);
      
      // Apply to effects chain
      if (effectsChainRef.current) {
        effectsChainRef.current.setAmpEnabled(settings.ampEnabled, {
          ampModel: settings.ampModel,
          cabinet: settings.cabinet,
          controls: settings.ampControls,
        });
        if (settings.pedals) {
          effectsChainRef.current.setPedals(settings.pedals);
        }
      }
    }
  }, []);
  
  // Handle amp setting changes
  const handleAmpChange = useCallback((key, value) => {
    setGuitarEffects(prev => {
      const updated = { ...prev };
      if (key === 'ampModel' || key === 'cabinet') {
        updated[key] = value;
      } else if (key === 'ampEnabled') {
        updated.ampEnabled = value;
      } else {
        updated.ampControls = { ...prev.ampControls, [key]: value };
      }
      
      // Apply to chain
      if (effectsChainRef.current) {
        if (key === 'ampEnabled') {
          effectsChainRef.current.setAmpEnabled(value, {
            ampModel: updated.ampModel,
            cabinet: updated.cabinet,
            controls: updated.ampControls,
          });
        } else if (key === 'ampModel') {
          effectsChainRef.current.setAmpSettings({ ampModel: value });
        } else if (key === 'cabinet') {
          effectsChainRef.current.setAmpSettings({ cabinet: value });
        } else {
          effectsChainRef.current.setAmpSettings({ controls: updated.ampControls });
        }
      }
      
      return updated;
    });
  }, []);
  
  // Handle pedal changes
  const handlePedalsChange = useCallback((pedals) => {
    setGuitarEffects(prev => ({ ...prev, pedals }));
    if (effectsChainRef.current) {
      effectsChainRef.current.setPedals(pedals);
    }
  }, []);
  
  // Save current settings as preset
  const handleSavePreset = async () => {
    if (!user?.id) {
      alert('Please log in to save presets');
      return;
    }
    
    const name = prompt('Enter preset name:');
    if (!name) return;
    
    try {
      const res = await fetch(`${API_URL}/api/guitar-presets/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          preset: {
            name,
            category: 'custom',
            description: 'Custom preset from Effects Lab',
            icon: '🎸',
            color: 'violet',
            isUserPreset: true,
            settings: {
              ampEnabled: guitarEffects.ampEnabled,
              ampModel: guitarEffects.ampModel,
              cabinet: guitarEffects.cabinet,
              ampControls: guitarEffects.ampControls,
              pedals: guitarEffects.pedals,
            }
          }
        })
      });
      
      if (res.ok) {
        alert('Preset saved successfully!');
        // Refresh presets
        const data = await res.json();
        setUserPresets(prev => [data.preset, ...prev]);
      }
    } catch (err) {
      console.error('Failed to save preset:', err);
      alert('Failed to save preset');
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-zinc-900/95 backdrop-blur border-b border-zinc-800">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Effects Lab
              </h1>
              <p className="text-xs text-zinc-400">Test guitar effects with real audio</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSavePreset}
              className="border-zinc-700"
            >
              <Save className="w-4 h-4 mr-1" />
              Save Preset
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Preset Selector */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
          <GuitarPresetSelector
            selectedPresetId={selectedPreset?.id}
            onSelectPreset={handlePresetSelect}
            userPresets={userPresets}
            userId={user?.id}
          />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Audio Test Panel */}
          <div>
            <AudioTestPanel
              guitarEffectsChain={effectsChainRef.current}
              onEffectsStatusChange={setEffectsStatus}
              currentPreset={selectedPreset}
            />
          </div>
          
          {/* Amp Simulator */}
          <div className="space-y-6">
            <GuitarAmpSimulator
              enabled={guitarEffects.ampEnabled}
              ampModel={guitarEffects.ampModel}
              cabinet={guitarEffects.cabinet}
              controls={guitarEffects.ampControls}
              onToggleEnabled={(enabled) => handleAmpChange('ampEnabled', enabled)}
              onModelChange={(model) => handleAmpChange('ampModel', model)}
              onCabinetChange={(cabinet) => handleAmpChange('cabinet', cabinet)}
              onControlChange={(key, value) => handleAmpChange(key, value)}
            />
          </div>
        </div>
        
        {/* Pedal Board */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
          <GuitarPedalBoard
            pedals={guitarEffects.pedals}
            onPedalsChange={handlePedalsChange}
          />
        </div>
        
        {/* Current Settings Display */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Current Configuration
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">Amp Model</span>
              <p className="text-white font-medium capitalize">{guitarEffects.ampModel}</p>
            </div>
            <div>
              <span className="text-zinc-500">Cabinet</span>
              <p className="text-white font-medium">{guitarEffects.cabinet}</p>
            </div>
            <div>
              <span className="text-zinc-500">Gain</span>
              <p className="text-white font-medium">{guitarEffects.ampControls?.gain || 50}%</p>
            </div>
            <div>
              <span className="text-zinc-500">Pedals</span>
              <p className="text-white font-medium">{guitarEffects.pedals?.length || 0} active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
